<template>
	<b-card class="completion-traffic-light-card">
		<div class="completion-traffic-light-card__content">
			<div class="completion-traffic-light-card__left">
				<div :class="['completion-traffic-light-card__indicator', `completion-traffic-light-card__indicator--${status}`]">
					{{ loading ? '--' : `${completionPercentage}%` }}
				</div>
				<p class="completion-traffic-light-card__summary mb-0">
					<template v-if="loading">
						<span>Loading completion summary</span>
						<span class="completion-traffic-light-card__loading-dots" aria-hidden="true">
							<span>.</span><span>.</span><span>.</span>
						</span>
					</template>
					<template v-else>
						{{ summaryText }}
					</template>
				</p>
			</div>

			<div class="completion-traffic-light-card__right">
				<div class="completion-traffic-light-card__right-content">
					<template v-if="showUpToDateCelebration">
						<div class="completion-traffic-light-card__celebration">
							<div class="completion-traffic-light-card__celebration-icons" aria-hidden="true">
								<div class="completion-traffic-light-card__smiley-wrap">
									<EmoticonHappyOutlineIcon class="completion-traffic-light-card__smiley" />
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--1"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--2"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--3"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--4"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--5"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--6"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--7"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--8"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--9"></span>
									<span class="completion-traffic-light-card__confetti-piece completion-traffic-light-card__confetti-piece--10"></span>
								</div>
							</div>
							<p class="completion-traffic-light-card__celebration-text mb-0">
								You're up to date on your CPD, good job!
							</p>
						</div>
					</template>
					<template v-else>
						<div v-if="loading" class="completion-traffic-light-card__spinner-overlay">
							<PulseLoader />
						</div>
						<p
							class="completion-traffic-light-card__estimate-text mb-2"
							:class="{ 'completion-traffic-light-card__placeholder': loading }"
						>
							Our CPDassist tool estimates that to bring all your entries {{ periodWording }} to 100% will take you
						</p>
						<div
							class="completion-traffic-light-card__time"
							:class="{ 'completion-traffic-light-card__placeholder': loading }"
						>
							<clock-outline-icon class="mr-2" />
							<span>{{ formattedTimeEstimate }}</span>
						</div>
					</template>
				</div>
			</div>
		</div>
	</b-card>
</template>

<script>
import PulseLoader from '@/components/loaders/Pulse.vue';
import EmoticonHappyOutlineIcon from 'vue-material-design-icons/EmoticonHappyOutline';
export default {
	name: 'CompletionTrafficLightCard',
	components: {
		PulseLoader,
		EmoticonHappyOutlineIcon
	},
	props: {
		period: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			loading: true,
			breakdown: {
				complete: 0,
				partiallyComplete: 0,
				incomplete: 0,
				total: 0
			}
		}
	},
	computed: {
		completionPercentage() {
			if (!this.breakdown.total) {
				return 0;
			}

			return Math.round((this.breakdown.complete / this.breakdown.total) * 100);
		},
		status() {
			if (this.completionPercentage === 100) {
				return 'complete';
			}

			if (this.completionPercentage > 50) {
				return 'partial';
			}

			return 'incomplete';
		},
		periodWording() {
			const customRange = this.getCustomDateRange(this.period);

			if (customRange) {
				return `from ${customRange.from.format('DD/MM/YYYY')} to ${customRange.to.format('DD/MM/YYYY')}`;
			}

			switch (this.period) {
				case '24months':
					return 'over the last 24 months';
				case '36months':
					return 'over the last 36 months';
				case 'lifetime':
					return 'for all time';
				case '12months':
				default:
					return 'over the last 12 months';
			}
		},
		summaryText() {

			if (!this.breakdown.total) {
				return `No entries ${this.periodWording}.`;
			}
			return `${this.completionPercentage}% of your entries ${this.periodWording} are complete`;
		},
		estimatedMinutes() {
			const incompleteCount = Math.max(0, parseInt(this.breakdown.incomplete, 10) || 0);
			const partiallyCompleteCount = Math.max(0, parseInt(this.breakdown.partiallyComplete, 10) || 0);
			return (incompleteCount * 10) + (partiallyCompleteCount * 5);
		},
		formattedTimeEstimate() {
			return this.formatEstimateTime(this.estimatedMinutes);
		},
		showUpToDateCelebration() {
			return !this.loading &&
				this.breakdown.total > 0 &&
				this.completionPercentage === 100 &&
				this.estimatedMinutes === 0;
		}
	},
	methods: {
		loadBreakdown() {
			this.loading = true;

			const endpoint = this.getEndpoint();
			this.$api.v2.get(endpoint).then((response) => {
				const payload = response.body || response.data || [];
				this.breakdown = this.parseBreakdown(payload);
			}).catch((resp) => {
				this.$httpError('Failed to load completion summary', resp);
				this.breakdown = {
					complete: 0,
					partiallyComplete: 0,
					incomplete: 0,
					total: 0
				};
			}).then(() => {
				this.loading = false;
			});
		},
		getEndpoint() {
			if (this.period && this.period.charAt(0) === '?') {
				return `statistics/v2/monthly${this.period}`;
			}

			return `statistics/v2/monthly?period=${encodeURIComponent(this.period)}`;
		},
		parseBreakdown(payload) {
			if (Array.isArray(payload)) {
				return this.aggregateMonthlyValues(payload);
			}

			if (payload && payload.summary) {
				return this.normaliseBreakdown(payload.summary);
			}

			return this.normaliseBreakdown(payload);
		},
		aggregateMonthlyValues(months) {
			return months.reduce((acc, month) => {
				const values = month.values || {};
				acc.complete += Number(values.complete || 0);
				acc.partiallyComplete += Number(values.partiallyComplete || 0);
				acc.incomplete += Number(values.incomplete || 0);
				acc.total += Number(values.total || 0);
				return acc;
			}, {
				complete: 0,
				partiallyComplete: 0,
				incomplete: 0,
				total: 0
			});
		},
		normaliseBreakdown(breakdown = {}) {
			return {
				complete: Number(breakdown.complete || 0),
				partiallyComplete: Number(breakdown.partiallyComplete || 0),
				incomplete: Number(breakdown.incomplete || 0),
				total: Number(breakdown.total || 0)
			};
		},
		getCustomDateRange(period) {
			if (!period || period.charAt(0) !== '?') {
				return null;
			}

			const params = new URLSearchParams(period.substring(1));
			const from = params.get('from');
			const to = params.get('to');

			if (!from || !to) {
				return null;
			}

			const fromMoment = this.moment(from, 'YYYY-MM-DD').startOf('day');
			const toMoment = this.moment(to, 'YYYY-MM-DD').endOf('day');

			if (!fromMoment.isValid() || !toMoment.isValid()) {
				return null;
			}

			return {
				from: fromMoment,
				to: toMoment
			};
		},
		formatEstimateTime(totalMinutes) {
			const safeTotalMinutes = Math.max(0, Math.trunc(Number(totalMinutes) || 0));
			const hours = Math.floor(safeTotalMinutes / 60);
			const minutes = safeTotalMinutes - (hours * 60);

			if (hours && minutes) {
				return `${hours}hr ${minutes}mins`;
			}

			if (hours) {
				return `${hours}hr`;
			}

			return `${minutes}mins`;
		}
	},
	watch: {
		period() {
			this.loadBreakdown();
		}
	},
	created() {
		this.loadBreakdown();
	}
}
</script>

<style scoped lang="scss">
.completion-traffic-light-card {
	border: 1px solid #8ba7b8;
	border-radius: 4px;
}

.completion-traffic-light-card__content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
}

.completion-traffic-light-card__left {
	display: flex;
	align-items: center;
	gap: 1rem;
	flex: 1;
}

.completion-traffic-light-card__right {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
}

.completion-traffic-light-card__right-content {
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	position: relative;
	min-height: 84px;
}

.completion-traffic-light-card__summary {
	font-weight: 600;
}

.completion-traffic-light-card__estimate-text {
	color: rgba(44, 62, 80, 0.8);
	text-align: center;
	width: 100%;
}

.completion-traffic-light-card__time {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 32px;
	font-size: 2rem;
	line-height: 1;
	color: #223f70;
}

.completion-traffic-light-card__spinner-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.completion-traffic-light-card__placeholder {
	visibility: hidden;
}

.completion-traffic-light-card__celebration {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.85rem;
}
.completion-traffic-light-card__celebration-icons {
	display: flex;
	align-items: center;
	justify-content: center;
}
.completion-traffic-light-card__smiley-wrap {
	position: relative;
	width: 72px;
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.completion-traffic-light-card__confetti-piece {
	position: absolute;
	top: 50%;
	left: 50%;
	width: 6px;
	height: 11px;
	border-radius: 1px;
	opacity: 0;
	transform: translate(-50%, -50%) scale(0.5);
	animation: completion-traffic-light-card-confetti-burst 900ms ease-out 1 forwards;
}

.completion-traffic-light-card__smiley {
	position: relative;
	z-index: 2;
	color: #99c140;
	width: 64px !important;
	height: 64px !important;
}

.completion-traffic-light-card__smiley svg {
	width: 64px !important;
	height: 64px !important;
}

.completion-traffic-light-card__confetti-piece--1 {
	--dx: -46px;
	--dy: -48px;
	--rot: -130deg;
	background: #99c140;
}

.completion-traffic-light-card__confetti-piece--2 {
	--dx: -26px;
	--dy: -56px;
	--rot: -80deg;
	background: #ffd166;
}

.completion-traffic-light-card__confetti-piece--3 {
	--dx: -58px;
	--dy: -22px;
	--rot: -160deg;
	background: #ef476f;
}

.completion-traffic-light-card__confetti-piece--4 {
	--dx: 44px;
	--dy: -52px;
	--rot: 120deg;
	background: #06d6a0;
}

.completion-traffic-light-card__confetti-piece--5 {
	--dx: 30px;
	--dy: -60px;
	--rot: 90deg;
	background: #118ab2;
}

.completion-traffic-light-card__confetti-piece--6 {
	--dx: 56px;
	--dy: -18px;
	--rot: 150deg;
	background: #ffd166;
}

.completion-traffic-light-card__confetti-piece--7 {
	--dx: -44px;
	--dy: 6px;
	--rot: -40deg;
	background: #06d6a0;
}

.completion-traffic-light-card__confetti-piece--8 {
	--dx: 44px;
	--dy: 8px;
	--rot: 35deg;
	background: #ef476f;
}

.completion-traffic-light-card__confetti-piece--9 {
	--dx: -8px;
	--dy: -62px;
	--rot: -10deg;
	background: #118ab2;
}

.completion-traffic-light-card__confetti-piece--10 {
	--dx: 8px;
	--dy: -64px;
	--rot: 14deg;
	background: #99c140;
}

.completion-traffic-light-card__celebration-text {
	font-weight: 700;
	color: #5f8d2b;
	text-align: left;
	line-height: 1.3;
	max-width: 260px;
}

.completion-traffic-light-card__loading-dots {
	display: inline-flex;
	align-items: center;
	margin-left: 2px;
}

.completion-traffic-light-card__loading-dots span {
	display: inline-block;
	opacity: 0.2;
	animation: completion-traffic-light-card-dot-pulse 1.2s infinite ease-in-out;
}

.completion-traffic-light-card__loading-dots span:nth-child(2) {
	animation-delay: 0.2s;
}

.completion-traffic-light-card__loading-dots span:nth-child(3) {
	animation-delay: 0.4s;
}

.completion-traffic-light-card__indicator {
	width: 56px;
	height: 56px;
	min-width: 56px;
	min-height: 56px;
	aspect-ratio: 1 / 1;
	flex: 0 0 56px;
	flex-shrink: 0;
	border-radius: 50%;
	border: 3px solid transparent;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 13px;
	font-weight: 700;
}

.completion-traffic-light-card__indicator--complete {
	border-color: #99c140;
	color: #99c140;
}

.completion-traffic-light-card__indicator--partial {
	border-color: #e7b416;
	color: #e7b416;
}

.completion-traffic-light-card__indicator--incomplete {
	border-color: #cc3232;
	color: #cc3232;
}

@keyframes completion-traffic-light-card-dot-pulse {
	0%, 80%, 100% {
		opacity: 0.2;
		transform: translateY(0);
	}
	40% {
		opacity: 1;
		transform: translateY(-1px);
	}
}

@keyframes completion-traffic-light-card-confetti-burst {
	0% {
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.5) rotate(0deg);
	}
	12% {
		opacity: 1;
	}
	100% {
		opacity: 0;
		transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1) rotate(var(--rot));
	}
}

@media screen and (max-width: 1024px) {
	.completion-traffic-light-card__content {
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.completion-traffic-light-card__left {
		width: 100%;
		justify-content: center;
	}

	.completion-traffic-light-card__right {
		width: 100%;
		align-items: center;
		text-align: center;
	}

	.completion-traffic-light-card__summary,
	.completion-traffic-light-card__estimate-text {
		text-align: center;
	}

	.completion-traffic-light-card__time {
		font-size: 1.5rem;
	}
}
</style>
